import React, { FC, ReactNode, useContext } from "react";
import { Profit } from "../../../../../../../interfaces/profit";
import { LocationDate, ModalContent, ModalFooter } from "./Styled";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { useApiError } from "../../../../../../../hooks/use-api-error";
import Button from "../../../../../../ui/button";
import { Form, Formik } from "formik";
import {
  formatCPF,
  formatDate,
  numberToCurrency,
} from "../../../../../../utils/formatters";
import moment from "moment";
import { useAuthContext } from "../../../../../../auth-provider";
import RadioGroup from "../../../../../../ui/radioGroup";
import { AppConfigsContext } from "../../../../../../app-configs-context";
import Alert from "../../../../../../ui/alert";
import { Link } from "react-router-dom";
import { OpenModalLink } from "../../../../../../ui/modal/Styled";

type Props = {
  profit: Profit;
  onFinish: () => void;
};

type Section = {
  type: "ul" | "ol" | "p";
  title?: string;
  items?: ReactNode[];
  content?: ReactNode;
};

type AppConfigs = {
  contact?: {
    email?: string;
    telefone?: string;
    numero_whatsapp?: string;
  };
};

const ProfitDecision: FC<Props> = ({ profit, onFinish }) => {
  const {
    state: { user },
  } = useAuthContext();

  const appConfigs = useContext<AppConfigs>(AppConfigsContext);

  const { email, telefone, numero_whatsapp } = appConfigs?.contact;

  const showError = useApiError();

  const { mutateAsync: handleSubmit, isLoading } = useMutation(
    async (option: "reinvest" | "redeem") => {
      await axios.patch(`/v1/profits/${profit.id}`, { option });
    },
    {
      onSuccess: () => {
        onFinish();
        toast.success("Sua escolha foi registrada com sucesso.");
      },
      onError: (err) => showError(err),
    }
  );

  const { name, nationality, rg_number, cpf } = user;

  const hasBankAccount = !!user.bank_account;

  const {
    gross_value_in_cents,
    net_value_in_cents,
    investable: { code, open_at },
    precatorio: { numero, tribunal, devedor, received_at },
    operation_financial_details: {
      total_invested,
      current_total_invested,
      current_precatory_value,
      current_precatory_percentage,
      investment_period,
      aliquot,
      net_return_percentage,
      net_annualized,
    },
    deadlines: {
      answer_deadline,
      guarantee_replacement_deadline,
      payment_deadline,
    },
  } = profit;

  const currentDate = moment().format("LL");

  const content: Section[] = [
    {
      type: "ul",
      title: "IDENTIFICAÇÃO",
      items: [
        <>
          <b>Emissor:</b> MERCATÓRIO SECURITIZADORA E INVESTIMENTOS
          PARTICIPATIVOS EM CRÉDITOS JUDICIAIS S/A, sociedade anônima, inscrita
          no CNPJ sob o nº 53.834.001/0001-06, com sede na Avenida Raja
          Gabaglia, n. 2000, Bairro Alpes, CEP 30494-170, Belo Horizonte/MG
          (“MERCATÓRIO”).
        </>,
        <>
          <b>Investidor:</b> {name}, {nationality}, portador(a) do RG nº{" "}
          {rg_number}, inscrito(a) no CPF sob o nº {formatCPF(cpf)}, doravante
          denominado(a) “Investidor”.
        </>,
        <>
          <b>Debênture:</b> Debênture Privada {code}, emitida pela MERCATÓRIO em{" "}
          {formatDate(open_at)}, lastreada no Precatório nº {numero} emitido
          pelo {tribunal}, tendo como devedor {devedor}.
        </>,
      ],
    },
    {
      type: "ol",
      title: "CONSIDERANDOS",
      items: [
        <>
          A MERCATÓRIO emitiu a Debênture Privada identificada acima, lastreada
          no precatório {numero}.
        </>,
        <>
          O precatório que serviu como lastro da debênture foi integralmente
          pago pelo Ente Público Devedor na data de {formatDate(received_at)}.
        </>,
        <>
          Conforme previsto nos Termos de Uso e no Contrato de Subscrição de
          Debêntures, o pagamento do precatório gera o direito de opção de
          reinvestimento (revolvência) ou resgate de recursos por parte do
          Investidor.
        </>,
      ],
    },
    {
      type: "ul",
      title: "DETALHAMENTO FINANCEIRO DA OPERAÇÃO",
      items: [
        <>Valor total investido: {numberToCurrency(total_invested)}</>,
        <>
          Valor total investido atualizado (bruto):{" "}
          {numberToCurrency(current_total_invested)}
        </>,
        <>Precatório pago: Precatório nº {numero}</>,
        <>
          Precatório pago em relação ao total das garantias:{" "}
          {numberToCurrency(current_precatory_value)} (
          {current_precatory_percentage}% do total)
        </>,
        <>
          Valor bruto a ser recebido/reinvestido:{" "}
          {numberToCurrency(gross_value_in_cents)}
        </>,
        <>Período de investimento: {investment_period} dias</>,
        <>
          Desconto de Imposto de Renda:{" "}
          {numberToCurrency(gross_value_in_cents - net_value_in_cents)} (
          {aliquot}%)
        </>,
        <>
          Valor Líquido a ser recebido: {numberToCurrency(net_value_in_cents)}
        </>,
        <>
          Valor Líquido a ser reinvestido:{" "}
          {numberToCurrency(gross_value_in_cents)}
        </>,
        // <>Retorno Líquido (ROI): {net_return_percentage}%</>,
        // <>Anualizado Líquido: {net_annualized}%</>,
      ],
    },
    {
      type: "ol",
      title: "CONDIÇÕES GERAIS",
      items: [
        <>
          O presente Termo é parte integrante do Boletim de Subscrição da
          Debênture Privada mencionada, complementando suas disposições.
        </>,
        <>
          A MERCATÓRIO se compromete a realizar esforços para identificar e
          substituir a garantia da debênture por um ativo com características de
          risco, prazo e rentabilidade similares às do ativo original que
          lastreava a debênture no momento da captação. Caso, após análise
          criteriosa, não seja possível encontrar um ativo equivalente no prazo
          de 20 (vinte) dias úteis, contados do pagamento do precatório, a{" "}
          <b>Opção 2 - Resgate</b> será automaticamente aplicada, conforme
          previsto neste Termo.
        </>,
        <>
          A escolha de uma das opções não invalida outras condições previamente
          estabelecidas no Boletim de Subscrição da Debênture.
        </>,
        <>
          <b>PRAZO PARA MANIFESTAÇÃO:</b> O Investidor deverá manifestar sua
          opção de resgate na Plataforma, de forma <i>expressa e inequívoca</i>{" "}
          até o dia {formatDate(answer_deadline)}, prazo final de 10 (dez) dias
          úteis após o recebimento do e-mail de notificação. O silêncio ou a
          omissão em manifestar a opção dentro deste prazo resultará na
          automática adesão ao reinvestimento dos recursos, conforme
          estabelecido neste Termo.
        </>,
        <>
          Eventuais dúvidas sobre este Termo e sobre os procedimentos a serem
          adotados poderão ser esclarecidas pela MERCATÓRIO por meio do e-mail:{" "}
          {email}, telefone: {telefone} e WhatsApp: {numero_whatsapp}.
        </>,
      ],
    },
    {
      type: "p",
      content: (
        <>
          O presente termo tem por objeto formalizar a opção do Investidor em
          relação aos valores referentes ao precatório pago que lastreia a
          Debênture Privada acima identificada, assegurando ao Investidor as
          seguintes alternativas:
        </>
      ),
    },
    {
      type: "ol",
      title: "OPÇÃO 1 - REINVESTIMENTO (REVOLVÊNCIA)",
      items: [
        <>
          O Investidor opta por manter o valor proporcional da sua subscrição da
          debênture investido na operação, autorizando a MERCATÓRIO a substituir
          a garantia da debênture até o dia{" "}
          {formatDate(guarantee_replacement_deadline)}, 20 (vinte) dias úteis a
          contar da data do pagamento do precatório.
        </>,
        <>
          Caso a MERCATÓRIO não efetue a substituição da garantia no prazo
          estipulado, o valor proporcional da subscrição será integralmente e
          compulsoriamente devolvido ao Investidor. A devolução dos recursos
          ocorrerá até o dia {formatDate(payment_deadline)}, 5 (cinco) dias
          úteis após o término do prazo de substituição da garantia.
        </>,
        <>
          A nova garantia será informada ao Investidor por meio de comunicação
          eletrônica (e-mail) e disponibilizada na Plataforma da MERCATÓRIO para
          consulta, quando implementada a substituição.
        </>,
        <>
          Caso o investidor opte pela revolvência, a debênture continuará
          gerando os rendimentos estabelecidos no Boletim de Subscrição,
          enquanto perdurar o processo de substituição do precatório pela
          Mercatório, mesmo na hipótese de não ser localizado outro precatório.
        </>,
        <>
          A nova garantia terá características de risco, prazo e rentabilidade
          semelhantes às do ativo original que lastreava a debênture no momento
          da captação. <b>Exemplo:</b> Se um ativo com vencimento em 19 meses e
          rendimento de IPCA + 14% for pago, ele será substituído por outro
          ativo com as mesmas características de prazo e rentabilidade. De
          qualquer forma, os novos ativos deverão ser quitados dentro do prazo
          de vencimento da debênture, que é de 60 (sessenta) meses a partir da
          data do investimento.
        </>,
        <>
          <b>
            O SILÊNCIO DO INVESTIDOR, APÓS O PRAZO DE 10 (DEZ) DIAS ÚTEIS PARA
            MANIFESTAÇÃO, SERÁ INTERPRETADO COMO ACEITAÇÃO TÁCITA DA OPÇÃO DE
            REINVESTIMENTO.
          </b>
        </>,
      ],
    },
    {
      type: "ol",
      title: "OPÇÃO 2 - RESGATE",
      items: [
        <>
          O Investidor, ao optar pelo resgate, manifesta formalmente o seu
          desejo de receber de volta o valor proporcional da sua subscrição na
          debênture, correspondente ao montante pago pelo Ente Público Devedor
          em relação ao precatório que lastreava a debênture.
        </>,
        <>
          A devolução do valor de resgate será efetuada em até 5 (cinco) dias
          úteis, contados a partir da data em que o Investidor manifestar
          expressamente sua opção de resgate na Plataforma. O pagamento será
          realizado por meio de transferência bancária para a conta corrente de
          titularidade do Investidor, conforme indicada no Boletim de Subscrição
          da Debênture.
        </>,
        <>
          O valor a ser devolvido ao Investidor corresponderá à sua participação
          proporcional na debênture, calculada com base no valor total da
          garantia original paga pelo Ente Público Devedor. O resgate abrangerá
          integralmente o valor correspondente à sua subscrição,{" "}
          <i>não sendo permitido o resgate parcial.</i>
        </>,
        <>
          Na opção pelo resgate, o valor a ser devolvido ao Investidor incluirá
          a atualização do rendimento da debênture até a data de notificação do
          pagamento do precatório pela Mercatório. O rendimento será calculado
          com base nas condições estabelecidas no Boletim de Subscrição da
          Debênture, proporcionalmente ao período entre a data da subscrição e a
          data da notificação.
        </>,
      ],
    },
  ];

  return (
    <ModalContent>
      {content.map(({ type, title, items, content }, index) => {
        if (["ol", "ul"].includes(type)) {
          const ListTag = type;
          return (
            <section key={index}>
              <h2>{title}</h2>
              <ListTag>
                {items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ListTag>
            </section>
          );
        }

        if (type === "p") {
          return <p key={index}>{content}</p>;
        }

        return null;
      })}

      <ModalFooter>
        <h2>MANIFESTAÇÃO DO INVESTIDOR</h2>
        <p>
          O Investidor declara ter ciência e concordar com os termos do presente
          documento e manifesta sua opção marcando a alternativa escolhida:
        </p>

        <Formik
          initialValues={{ option: "" }}
          onSubmit={({ option }) =>
            handleSubmit(option as "reinvest" | "redeem")
          }
        >
          {({ values, setFieldValue, isSubmitting }) => (
            <Form>
              {!hasBankAccount && (
                <Alert
                  level="info"
                  notDismissable
                  message={
                    <span>
                      Você precisa preencher suas{" "}
                      <strong>informações bancárias</strong> para continuar.{" "}
                      <OpenModalLink as={Link} to="/meu_perfil">
                        Ir para meu perfil
                      </OpenModalLink>
                    </span>
                  }
                />
              )}

              <RadioGroup
                disabled={!hasBankAccount}
                currentValue={values.option}
                onValueChange={(value) => setFieldValue("option", value)}
                options={[
                  {
                    label: "OPÇÃO 1 - REINVESTIMENTO",
                    value: "reinvest",
                  },
                  {
                    label: "OPÇÃO 2 - RESGATE",
                    value: "redeem",
                  },
                ]}
              />

              <LocationDate>Belo Horizonte - MG, {currentDate}</LocationDate>

              <Button
                type="submit"
                variant="primary"
                disabled={!hasBankAccount || !values.option || isLoading}
                isSubmitting={isSubmitting}
              >
                CONFIRMAR ESCOLHA
              </Button>
            </Form>
          )}
        </Formik>
      </ModalFooter>
    </ModalContent>
  );
};

export default ProfitDecision;
